import { of as observableOf, Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { PeopleService } from './people.service';
import { ApiService } from './api.service';
import { ApiMode } from '../../utils/enums';
import { OfflineApiService } from './offlineApi.service';
import { AppConstants } from '../../utils/appConstants.class';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly templateMatcher: RegExp = /{{\s?([^{}\s]*)\s?}}/g;
  constructor(
    private translate: TranslateService,
    private peopleService: PeopleService,
    private apiService: ApiService,
    private offlineApi: OfflineApiService
  ) {
    this.translate.onLangChange.subscribe((value) => {
      localStorage.setItem(AppConstants.CURRENT_LANGUAGE, value.lang);
      this.languageChange.next(value.lang);
    });
  }

  private isLanguageListLoaded = false;
  private languageList: any[];
  private languageChange = new Subject<any>();
  private readonly languages = [
    { label: 'English', value: 'en' },
    { label: 'Deutsch', value: 'de' },
    { label: 'Español', value: 'es' },
    { label: 'Français', value: 'fr' },
    { label: 'Italiano', value: 'it' },
    { label: '中文', value: 'zh' },
    { label: '日本語', value: 'jp' },
    { label: 'русский', value: 'ru' },
    { label: 'Portuguese', value: 'pt' },
    { label: 'Polski', value: 'pl' },
    { label: 'Czech', value: 'cs' }
  ];

  initLanguage(): void {
    this.translate.setDefaultLang('en');
    this.peopleService.getPersonLanguage().subscribe((language) => {
      language ? this.translate.use(language) : this.translate.use('en');
      language
        ? localStorage.setItem(AppConstants.CURRENT_LANGUAGE, language)
        : localStorage.setItem(AppConstants.CURRENT_LANGUAGE, 'en');
    });
  }

  set(language: string): void {
    this.translate.use(language);
    localStorage.setItem(AppConstants.CURRENT_LANGUAGE, language);
  }

  loadTranslations(lang: string): Observable<any> {
    if (!this.translate.translations[lang]) {
      return this.translate.getTranslation(lang).pipe(
        map((data) => {
          this.translate.setDefaultLang('en');
          this.translate.use(lang);
          return data;
        })
      );
    } else {
      return observableOf({});
    }
  }

  getLanguages() {
    if (this.isLanguageListLoaded) {
      return observableOf(this.languageList);
    } else {
      return this.offlineApi.getWithMode('/languages', ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.languages).pipe(
        map((data) => {
          this.languageList = data;
          this.isLanguageListLoaded = true;
          return data;
        })
      );
    }
  }

  public getLanguageList() {
    return this.getLanguages().pipe(
      map((data) => {
        const availableLanguages = data;
        return this.languages.filter((l) => availableLanguages.some((a) => a.toLowerCase() === l.value));
      })
    );
  }

  get() {
    return this.translate.currentLang;
  }

  getIso2() {
    // warning : Japan country ISO is 'jp' but language ISO is 'ja'
    const currentLang = this.translate.currentLang;
    return currentLang === 'jp' ? 'ja' : currentLang;
  }

  getValueByKey(key: string, params?: any) {
    return this.translate.get(key, params);
  }

  getInstantValue(key: string, params?: any) {
    return this.translate.instant(key, params);
  }

  getNestedValue(key: string, params?: any) {
    let value = this.translate.instant(key, params);
    return value.replace(this.templateMatcher, (substring: string, b: string) => {
      return this.getNestedValue(b);
    });
  }

  public downloadTranslations(): any {
    const url = 'api/translations/download';
    window.open(url, '_blank');
  }

  public uploadTranslations(files: any): Observable<any> {
    const url = '/translations/upload';
    return this.apiService.postFormData(url, files).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public uploadTranslationFiles(files: any): Observable<any> {
    const url = '/translationfiles/upload';
    return this.apiService.postFormData(url, files).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  public downloadTranslationFiles(files: string[]): void {
    const url = 'api/translationfiles/download/' + files.join(',');
    window.open(url, '_blank');
  }

  public onLanguageChange() {
    return this.languageChange.asObservable();
  }

  public getTranslations(language: string) {
    const url = `/inspectionreport/i18n/${language}`;
    return this.offlineApi.getWithMode(url, ApiMode.Lazy, AppConstants.INDEXEDDB_TABLES.translations, language);
  }
}
